'use client'

import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react'
import { api } from '@/lib/api'
import { getTenantCacheKey } from '@/lib/tenantCache'

interface TenantInfo {
  id?: string
  name: string
  logo_url?: string
  address?: string
  phone?: string
  whatsapp?: string
  instagram?: string
  facebook?: string
  maps_url?: string
  welcome_message?: string
  template?: 'barbershop' | 'studio' | 'agenda'
  frontend_target?: string
  slug?: string
}

interface TenantContextType {
  tenant: TenantInfo | null
  loading: boolean
  error: Error | null
  refetch: () => Promise<void>
}

const TenantContext = createContext<TenantContextType | undefined>(undefined)

const TENANT_CACHE_KEY = 'tenant_info_cache'
const CACHE_DURATION = 5 * 60 * 1000 // 5 minutos

export function TenantProvider({ children, skipFetch = false, initialTenant = null }: { children: ReactNode; skipFetch?: boolean; initialTenant?: TenantInfo | null }) {
  const [tenant, setTenant] = useState<TenantInfo | null>(initialTenant)
  const [loading, setLoading] = useState(!initialTenant)
  const [error, setError] = useState<Error | null>(null)

  const loadTenant = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)

      if (typeof window !== 'undefined') {
        const cached = localStorage.getItem(getTenantCacheKey(TENANT_CACHE_KEY))
        if (cached) {
          try {
            const { data, timestamp } = JSON.parse(cached)
            const now = Date.now()

            if (now - timestamp < CACHE_DURATION) {
              setTenant(data)
              setLoading(false)
              if (!skipFetch) {
                api.get('/public/barbershop').then(freshData => {
                  setTenant(freshData)
                  localStorage.setItem(getTenantCacheKey(TENANT_CACHE_KEY), JSON.stringify({
                    data: freshData,
                    timestamp: Date.now()
                  }))
                }).catch(() => {})
              }
              return
            }
          } catch {
            localStorage.removeItem(getTenantCacheKey(TENANT_CACHE_KEY))
          }
        }
      }

      if (skipFetch) {
        setLoading(false)
        return
      }

      const data = await api.get('/public/barbershop')
      setTenant(data)

      if (typeof window !== 'undefined') {
        localStorage.setItem(getTenantCacheKey(TENANT_CACHE_KEY), JSON.stringify({
          data,
          timestamp: Date.now()
        }))
      }
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Error loading tenant'))
      console.error('Error loading tenant:', err)
    } finally {
      setLoading(false)
    }
  }, [skipFetch])

  useEffect(() => {
    loadTenant()
  }, [loadTenant])

  const refetch = useCallback(async () => {
    if (typeof window !== 'undefined') {
      localStorage.removeItem(getTenantCacheKey(TENANT_CACHE_KEY))
    }
    await loadTenant()
  }, [loadTenant])

  return (
    <TenantContext.Provider value={{ tenant, loading, error, refetch }}>
      {children}
    </TenantContext.Provider>
  )
}

export function useTenant() {
  const context = useContext(TenantContext)
  if (context === undefined) {
    throw new Error('useTenant must be used within a TenantProvider')
  }
  return context
}
